import { Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useOrganization } from "../../context/OrganizationContext";
import { billingService } from "../../services/billingService";
import { BillingCycle, BillingInfo, PlanType } from "../../types/billing";
import { SubscriptionManager } from "./SubscriptionManager";
import { PlanOverview } from "./PlanOverview";
import { ScheduledDowngradeNotice } from "./ScheduledDowngradeNotice";
import { ScheduleConflictDialog } from "./ScheduleConflictDialog";
import { TrialBanner } from "./TrialBanner";
import { UsageLimits } from "./UsageLimits";

export const BillingDashboard: React.FC = () => {
  const { selectedOrg } = useOrganization();
  const [billingData, setBillingData] = useState<BillingInfo | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [reconciliationStatus, setReconciliationStatus] = useState<{
    status: string;
    message: string;
    available_seats?: number;
  } | null>(null);
  const [scheduleConflict, setScheduleConflict] = useState<{
    existingChange: any;
    proposedChange: any;
    newPlan: PlanType;
    editorCount?: number;
    newBillingCycle?: BillingCycle;
  } | null>(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    if (selectedOrg) {
      fetchBillingLimits();
    }
  }, [selectedOrg]);

  // Refresh billing data when returning from payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");

    if (status === "success") {
      if (selectedOrg) {
        setSuccess(
          "Subscription updated successfully! Your plan will be active shortly.",
        );

        // Refresh billing data after a short delay to ensure webhook has processed
        setTimeout(() => {
          fetchBillingLimits();
        }, 2000); // 2 second delay

        // Also refresh again after a longer delay in case webhook is slow
        setTimeout(() => {
          fetchBillingLimits();
        }, 5000); // 5 second delay
      }
    }
  }, [selectedOrg]); // Re-run when selectedOrg changes

  const fetchBillingLimits = async () => {
    if (!selectedOrg) return;

    try {
      setLoading(true);
      const data = await billingService.getBillingLimits(selectedOrg.id);
      setBillingData(data);

      // Use billing cycle from API response, fallback to monthly
      if (data.billing_cycle) {
        setBillingCycle(data.billing_cycle as BillingCycle);
      } else {
        setBillingCycle("monthly");
      }

      // Reconcile editor counts if organization has Stripe billing
      if (data.billing_type?.toLowerCase() === "stripe") {
        try {
          const reconciliation = await billingService.reconcileEditorCount(
            selectedOrg.id,
          );

          if (reconciliation.status === "reconciled") {
            setReconciliationStatus(reconciliation);
            // Refresh billing data to show updated counts
            const updatedData = await billingService.getBillingLimits(
              selectedOrg.id,
            );
            setBillingData(updatedData);
          } else if (reconciliation.status === "prepaid") {
            // Don't refresh - just note the prepaid seats
            setReconciliationStatus(reconciliation);
          }
        } catch (reconcileError) {
          // console.error removed
          // Don't fail the whole page load if reconciliation fails
        }
      }
    } catch (error: any) {
      // console.error removed
      setError("Failed to load billing information");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planType: PlanType, cycle: BillingCycle) => {
    if (!selectedOrg || !billingData) return;

    setError(null);
    setSuccess(null);

    try {
      setActionLoading(true);

      // Get current editor count for pricing
      const editorCount = billingData.current_editors || 1;

      // Check if we have an active subscription - if so, use upgrade endpoint
      const hasActiveSubscription =
        billingData.subscription_status &&
        billingData.subscription_status?.toLowerCase() !== "canceled" &&
        billingData.plan_type?.toLowerCase() !== "free";

      if (hasActiveSubscription) {
        // Use upgrade endpoint for existing subscriptions
        const result = await billingService.upgradeSubscription(
          selectedOrg.id,
          planType,
          cycle,
          editorCount,
        );

        setSuccess(result.message || "Subscription upgraded successfully");

        // Refresh billing data to show updated plan
        await fetchBillingLimits();
      } else {
        // Create new subscription via checkout
        const session = await billingService.createSubscription(
          selectedOrg.id,
          planType,
          cycle,
          editorCount,
        );

        // Redirect to Stripe Checkout
        window.location.href = session.checkout_url;
      }
    } catch (error: any) {
      // console.error removed
      setError(
        error.response?.data?.detail || "Failed to start upgrade process",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleDowngrade = async (
    newPlan: PlanType,
    editorCount?: number,
    newBillingCycle?: BillingCycle,
    force = false,
  ) => {
    if (!selectedOrg) return;

    setError(null);
    setSuccess(null);

    try {
      setActionLoading(true);
      const result = await billingService.scheduleDowngrade(
        selectedOrg.id,
        newPlan,
        editorCount,
        newBillingCycle,
        force,
      );

      // Debug logging
      console.log("Schedule downgrade result:", result);
      console.log(
        "Result status:",
        "status" in result ? result.status : "no status",
      );
      console.log(
        "Is conflict?",
        "status" in result && result.status === "conflict",
      );

      // Check if there's a conflict
      if ("status" in result && result.status === "conflict") {
        console.log("Conflict detected, showing dialog");
        // Store conflict info and show dialog
        setScheduleConflict({
          existingChange: result.existing_change || {},
          proposedChange: result.proposed_change || {},
          newPlan: newPlan,
          editorCount: editorCount,
          newBillingCycle: newBillingCycle,
        });
        setShowConflictDialog(true);
      } else {
        console.log("No conflict, scheduling downgrade");
        // Success - downgrade scheduled
        setSuccess("Plan change scheduled successfully");

        // Refresh billing data
        await fetchBillingLimits();
      }
    } catch (error: any) {
      // console.error removed
      setError(error.response?.data?.detail || "Failed to schedule downgrade");
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    if (!selectedOrg) return;

    setError(null);
    setSuccess(null);

    try {
      setActionLoading(true);
      const session = await billingService.createPortalSession(
        selectedOrg.id,
        window.location.href,
      );

      // Redirect to Stripe Customer Portal
      window.location.href = session.portal_url;
    } catch (error: any) {
      // console.error removed
      setError(error.response?.data?.detail || "Failed to open billing portal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartTrial = async () => {
    if (!selectedOrg) return;

    setError(null);
    setSuccess(null);

    try {
      setActionLoading(true);
      await billingService.startTrial(selectedOrg.id);
      setSuccess("Trial started successfully!");

      // Refresh billing data
      await fetchBillingLimits();
    } catch (error: any) {
      // console.error removed
      setError(error.response?.data?.detail || "Failed to start trial");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedOrg) return;

    setError(null);
    setSuccess(null);
    setShowCancelDialog(false);

    try {
      setActionLoading(true);
      const result = await billingService.cancelSubscription(
        selectedOrg.id,
        true, // Cancel at period end
      );
      setSuccess(result.message);

      // Refresh billing data
      await fetchBillingLimits();
    } catch (error: any) {
      // console.error removed
      setError(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!selectedOrg) return;

    setError(null);
    setSuccess(null);

    try {
      setActionLoading(true);
      const result = await billingService.reactivateSubscription(
        selectedOrg.id,
      );
      setSuccess(result.message || "Subscription reactivated successfully");

      // Refresh billing data
      await fetchBillingLimits();
    } catch (error: any) {
      // console.error removed
      setError(
        error.response?.data?.detail || "Failed to reactivate subscription",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleBillingCycleChange = async (
    newBillingCycle: BillingCycle,
    force = false,
  ) => {
    if (!selectedOrg) return;
    setError(null);
    setSuccess(null);
    try {
      setActionLoading(true);
      const result = await billingService.changeBillingCycle(
        selectedOrg.id,
        newBillingCycle,
        force,
      );

      // Check if there's a conflict
      if ("status" in result && result.status === "conflict") {
        // Store conflict info and show dialog
        setScheduleConflict({
          existingChange: result.existing_change || {},
          proposedChange: result.proposed_change || {},
          newPlan: (billingData?.plan_type || "free") as PlanType, // Keep current plan
          editorCount: billingData?.current_editors || 1, // Keep current editors
          newBillingCycle: newBillingCycle,
        });
        setShowConflictDialog(true);
      } else {
        // Success - billing cycle change scheduled
        setSuccess("Billing cycle change scheduled successfully");
        // Refresh billing data
        await fetchBillingLimits();
      }
    } catch (error: any) {
      setError(
        error.response?.data?.detail ||
          "Failed to schedule billing cycle change",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="mt-6 h-48 bg-gray-200 rounded-lg"></div>
          <div className="mt-6 h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!billingData || !selectedOrg) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No billing information available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Editor Count Reconciliation Notice */}
      {reconciliationStatus && reconciliationStatus.status === "reconciled" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                Editor count synchronized with your team members. Your
                subscription was automatically updated to match your actual
                editor count.
              </p>
            </div>
            <button
              onClick={() => setReconciliationStatus(null)}
              className="ml-auto flex-shrink-0"
            >
              <svg
                className="h-4 w-4 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Prepaid Seats Notice */}
      {reconciliationStatus && reconciliationStatus.status === "prepaid" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">
                You have {reconciliationStatus.available_seats} prepaid editor
                seat
                {reconciliationStatus.available_seats !== 1 ? "s" : ""}{" "}
                available. You can invite new team members without additional
                charges.
              </p>
            </div>
            <button
              onClick={() => setReconciliationStatus(null)}
              className="ml-auto flex-shrink-0"
            >
              <svg
                className="h-4 w-4 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      <TrialBanner
        isTrialActive={billingData.is_trial_active}
        trialEndDate={billingData.trial_end_date}
        hasGenAiAccess={billingData.has_genai_access}
        hasActiveSubscription={
          !!billingData.subscription_status &&
          billingData.subscription_status?.toLowerCase() !== "canceled" &&
          billingData.plan_type?.toLowerCase() !== "free"
        }
      />

      {/* Free plan trial option - Prominently displayed for free plan users */}
      {billingData.plan_type?.toLowerCase() === "free" &&
        !billingData.is_trial_active && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 text-center border-2 border-blue-300 shadow-sm">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Unlock AI-Powered Research
            </h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              Start your {billingData.default_trial_days}-day free trial to
              access GenAI features that help you research markets, analyze
              competitors, and build better strategies.
            </p>
            <button
              onClick={handleStartTrial}
              disabled={actionLoading}
              className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Start Free Trial
            </button>
            <p className="text-xs text-gray-500 mt-3">
              No credit card required
            </p>
          </div>
        )}

      {/* Scheduled Downgrade Notice */}
      {billingData.scheduled_changes && (
        <ScheduledDowngradeNotice
          scheduledChanges={billingData.scheduled_changes}
          onCancel={async () => {
            try {
              setActionLoading(true);
              const result = await billingService.cancelScheduledChange(
                selectedOrg.id,
              );

              // Refresh billing data to get updated state
              await fetchBillingLimits();

              // Show success message
              setError(null);
              // You might want to add a success message state here
              console.log(result.message);
            } catch (error: any) {
              setError(
                error.response?.data?.detail ||
                  "Failed to cancel scheduled change",
              );
            } finally {
              setActionLoading(false);
            }
          }}
        />
      )}

      {/* Plan Overview */}
      <PlanOverview
        planType={billingData.plan_type}
        billingType={billingData.billing_type}
        subscriptionStatus={billingData.subscription_status}
        currentPeriodEnd={billingData.current_period_end}
      />

      {/* No Active Subscription Notice - for orgs set to Stripe but without subscription */}
      {billingData.billing_type?.toLowerCase() === "stripe" &&
        billingData.plan_type?.toLowerCase() !== "free" &&
        !billingData.subscription_status && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-amber-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-900">
                  No Active Subscription
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    Your organization is on the {billingData.plan_type} plan but
                    doesn&apos;t have an active subscription. Set up your
                    subscription to ensure continuous access.
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() =>
                      handleUpgrade(
                        billingData.plan_type as PlanType,
                        billingCycle,
                      )
                    }
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                  >
                    Set Up Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Manual Billing Notice */}
      {(billingData.billing_type?.toLowerCase() === "manual" ||
        billingData.billing_type?.toLowerCase() === "custom") && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-900">
                Manually Managed Billing
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Your organization&apos;s billing is managed directly with our
                  sales team. For billing inquiries or to transition to
                  self-service billing, please contact your account manager.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Limits */}
      <UsageLimits
        currentUsers={billingData.current_users}
        currentEditors={billingData.current_editors}
        currentPropositions={billingData.current_propositions}
        currentSegments={billingData.current_segments}
        maxUsers={billingData.max_users}
        maxEditors={billingData.max_editors}
        maxPropositions={billingData.max_propositions}
        maxSegments={billingData.max_segments}
      />

      {/* Limit Exceeded Warning */}
      {billingData.billing_type?.toLowerCase() === "stripe" &&
        billingData.plan_type?.toLowerCase() !== "professional" &&
        ((billingData.max_users &&
          billingData.current_users >= billingData.max_users) ||
          (billingData.max_editors &&
            billingData.current_editors >= billingData.max_editors) ||
          (billingData.max_propositions &&
            billingData.current_propositions >= billingData.max_propositions) ||
          (billingData.max_segments &&
            billingData.current_segments >= billingData.max_segments)) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-amber-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-900">
                  Plan Limits Reached
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    You&apos;ve reached one or more limits on your{" "}
                    {billingData.plan_type} plan. Use the Manage Subscription
                    section below to upgrade to Professional for unlimited
                    access.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Unified Subscription Manager - Show for all except manual billing */}
      {billingData.billing_type?.toLowerCase() !== "manual" &&
        billingData.billing_type?.toLowerCase() !== "custom" && (
          <SubscriptionManager
            organizationId={selectedOrg.id}
            currentPlan={billingData.plan_type as PlanType}
            currentBillingCycle={billingCycle}
            currentEditors={billingData.current_editors || 1}
            actualEditorCount={billingData.actual_editor_count}
            maxEditors={billingData.max_editors}
            isManualBilling={false}
            hasActiveSubscription={
              !!billingData.subscription_status &&
              billingData.subscription_status?.toLowerCase() !== "canceled"
            }
            scheduledChanges={billingData.scheduled_changes}
            onUpdate={fetchBillingLimits}
            onScheduleDowngrade={handleScheduleDowngrade}
            onScheduleBillingCycleChange={handleScheduleBillingCycleChange}
            isScheduledForCancellation={billingData.cancel_at_period_end}
          />
        )}

      {/* Manage Billing Button - Show for any organization with Stripe billing */}
      {billingData.billing_type?.toLowerCase() === "stripe" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Manage in Stripe
          </h3>
          {/* Show billing info if available */}
          {billingData.subscription_status &&
            billingData.current_period_end && (
              <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">
                    {billingData.cancel_at_period_end
                      ? "Cancels on:"
                      : "Next billing date:"}
                  </span>
                  <span className="ml-2 font-medium">
                    {new Date(
                      billingData.current_period_end,
                    ).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
                {billingData.plan_type !== "free" && (
                  <div>
                    <span className="text-gray-500">Current plan:</span>
                    <span className="ml-2 font-medium">
                      {billingData.plan_type?.charAt(0).toUpperCase() +
                        billingData.plan_type?.slice(1)}{" "}
                      ({billingCycle})
                    </span>
                  </div>
                )}
              </div>
            )}

          {/* Show cancellation notice if scheduled for cancellation */}
          {billingData.cancel_at_period_end && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800 font-medium">
                Subscription scheduled for cancellation
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Your subscription will end on{" "}
                {new Date(billingData.current_period_end!).toLocaleDateString(
                  "en-GB",
                  {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  },
                )}
                . You can reactivate anytime before this date.
              </p>
            </div>
          )}
          {!billingData.stripe_customer_id ? (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                No Stripe customer account found. Please set up a subscription
                first.
              </p>
            </div>
          ) : !billingData.subscription_status ? (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                No active subscription found. You can access your billing
                history, update payment methods, or reactivate a subscription in
                the Stripe portal.
              </p>
            </div>
          ) : null}
          <button
            onClick={handleManageBilling}
            disabled={actionLoading || !billingData.stripe_customer_id}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
            Manage Billing & Payment Methods
          </button>
          <p className="mt-2 text-xs text-gray-500 text-center">
            Update payment methods, download invoices, and manage your
            subscription
          </p>

          {/* Cancel/Reactivate Subscription Button */}
          {billingData.subscription_status &&
            billingData.subscription_status !== "canceled" &&
            billingData.plan_type !== "free" && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                {billingData.cancel_at_period_end ? (
                  <>
                    <button
                      onClick={handleReactivateSubscription}
                      disabled={actionLoading}
                      className="w-full flex items-center justify-center px-4 py-2 border border-green-300 rounded-md shadow-sm text-sm font-medium text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Reactivate Subscription
                    </button>
                    <p className="mt-2 text-xs text-gray-500 text-center">
                      Continue your subscription beyond the current period
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setShowCancelDialog(true)}
                      disabled={actionLoading}
                      className="w-full flex items-center justify-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Cancel Subscription
                    </button>
                    <p className="mt-2 text-xs text-gray-500 text-center">
                      Cancel at the end of your current billing period
                    </p>
                  </>
                )}
              </div>
            )}
        </div>
      )}

      {/* Schedule Conflict Dialog */}
      {scheduleConflict && (
        <ScheduleConflictDialog
          open={showConflictDialog}
          onClose={() => {
            setShowConflictDialog(false);
            setScheduleConflict(null);
          }}
          onConfirm={async () => {
            setShowConflictDialog(false);
            if (scheduleConflict) {
              // Check if this is a billing cycle-only change
              const isBillingCycleOnly =
                scheduleConflict.newPlan === billingData.plan_type &&
                scheduleConflict.editorCount === billingData.current_editors;

              if (isBillingCycleOnly && scheduleConflict.newBillingCycle) {
                // Call billing cycle handler with force=true
                await handleScheduleBillingCycleChange(
                  scheduleConflict.newBillingCycle,
                  true,
                );
              } else {
                // Call handleScheduleDowngrade with force=true for plan changes
                await handleScheduleDowngrade(
                  scheduleConflict.newPlan,
                  scheduleConflict.editorCount,
                  scheduleConflict.newBillingCycle,
                  true,
                );
              }
              setScheduleConflict(null);
            }
          }}
          existingChange={scheduleConflict.existingChange}
          proposedChange={scheduleConflict.proposedChange}
          loading={actionLoading}
        />
      )}

      {/* Cancel Subscription Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={() => setShowCancelDialog(false)}
            />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Cancel Subscription
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to cancel your subscription? Your
                        subscription will remain active until the end of your
                        current billing period.
                      </p>
                      {billingData?.current_period_end && (
                        <p className="mt-2 text-sm text-gray-500">
                          Your access will continue until{" "}
                          <span className="font-medium">
                            {new Date(
                              billingData.current_period_end,
                            ).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                        </p>
                      )}
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-800">
                          <strong>After cancellation:</strong>
                        </p>
                        <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                          <li>Your plan will revert to Free</li>
                          <li>
                            You&apos;ll keep 1 user, 1 proposition, and 1
                            segment
                          </li>
                          <li>AI features will be disabled</li>
                          <li>You can resubscribe anytime</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleCancelSubscription}
                  disabled={actionLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? "Canceling..." : "Cancel Subscription"}
                </button>
                <button
                  onClick={() => setShowCancelDialog(false)}
                  disabled={actionLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Keep Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
